/**
 * Loads, edits, and saves the `text_notes` section of the per-facility
 * delivery-steps config. Edits the document title, status source field, the
 * Eligible PA Statuses buckets, the routing rules, the pre-conditions, and the
 * run conditions;
 * everything else in the section (enabled/action/settings/document content) is
 * preserved and written back untouched via a partial Firestore update.
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../api/firebase/references";
import {
  DEFAULT_STALE_AFTER_MINUTES,
  DeliveryStepsDoc,
  TextNotesConfig,
  TrackStatusSettings,
} from "../../../../../data-model/deliveryStepsConfig";
import { QueryGroup } from "../../../../../data-model/queryBuilder";
import { LocalStorageKeys } from "../../../../../enums/localStorageKeys";
import { getItemFromLocalStorage } from "../../../../../utils/localStorageHelper";
import {
  createPaStatusRow,
  paStatusOptionsToRows,
  PaStatusRow,
  rowsToPaStatusOptions,
} from "../shared/paStatusRows";
import {
  queryValueToRunWhen,
  runWhenToQueryValue,
} from "../shared/runWhenConversion";
import { StepSettingsFormValues } from "../shared/stepSettingsModal";
import {
  contentFromConfig,
  contentToConfig,
  TextNotesContentForm,
} from "./textNotesContentUtils";
import {
  createEmptyRoutingRow,
  routingFromConfig,
  RoutingRuleRow,
  routingToConfig,
} from "./textNotesRoutingUtils";
import {
  pruneDeletedWhatWeAddLabel,
  stampSelectedWhatWeAddLabels,
} from "./textNotesWhatWeAddOptions";

interface TextNotesForm {
  enabled: boolean;
  action: string;
  statusField: string;
  settings: TrackStatusSettings;
  documentName: string;
  statusSourceField: string;
  paStatusRows: PaStatusRow[];
  routingRows: RoutingRuleRow[];
  content: TextNotesContentForm;
  preconditions: QueryGroup;
  conditions: QueryGroup;
}

const serialize = (form: TextNotesForm): string => JSON.stringify(form);

function formFromConfig(config: TextNotesConfig | undefined): TextNotesForm {
  const doc = config?.document_config;
  return {
    enabled: config?.enabled ?? false,
    action: config?.action ?? "fetch",
    statusField: config?.status_field ?? "",
    settings: { ...(config?.settings ?? {}) },
    documentName: doc?.title_template ?? "",
    statusSourceField: doc?.status_source_field ?? "",
    paStatusRows: paStatusOptionsToRows(doc?.pa_status_title_options),
    routingRows: routingFromConfig(doc?.routing),
    content: contentFromConfig(doc?.content),
    preconditions: runWhenToQueryValue(config?.preconditions),
    conditions: runWhenToQueryValue(config?.run_when),
  };
}

/**
 * Hook driving the Text Notes tab.
 *
 * @returns Editable form state, dirty/loading/saving flags, and mutators
 */
export function useTextNotesConfig() {
  const [originalDoc, setOriginalDoc] = useState<DeliveryStepsDoc | null>(null);
  const [form, setForm] = useState<TextNotesForm>(() =>
    formFromConfig(undefined),
  );
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgId =
    getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID) ?? "";

  const load = useCallback(async () => {
    if (!orgId.trim()) {
      setError("No healthcare facility id in local storage.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const path = FirestoreDocumentReference.deliveryStepsConfig(orgId);
      const doc =
        await FirestoreService.getDocumentOrNull<DeliveryStepsDoc>(path);
      setOriginalDoc(doc);
      const initial = formFromConfig(doc?.text_notes);
      setForm(initial);
      setSavedSnapshot(serialize(initial));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty = useMemo(
    () => serialize(form) !== savedSnapshot,
    [form, savedSnapshot],
  );

  const patch = useCallback(
    (changes: Partial<TextNotesForm>) => setForm((f) => ({ ...f, ...changes })),
    [],
  );

  const applySettingsForm = useCallback(
    (values: StepSettingsFormValues) => setForm((f) => ({ ...f, ...values })),
    [],
  );

  const setContent = useCallback(
    (content: TextNotesContentForm) => setForm((f) => ({ ...f, content })),
    [],
  );

  const updateRow = useCallback(
    (id: string, changes: Partial<PaStatusRow>) =>
      setForm((f) => ({
        ...f,
        paStatusRows: f.paStatusRows.map((r) =>
          r.id === id ? { ...r, ...changes } : r,
        ),
      })),
    [],
  );

  const addRow = useCallback(
    (data: { bucket: string; alias: string; includeDrugName: boolean }) =>
      setForm((f) => ({
        ...f,
        paStatusRows: [...f.paStatusRows, createPaStatusRow(data)],
      })),
    [],
  );

  const removeRow = useCallback(
    (id: string) =>
      setForm((f) => ({
        ...f,
        paStatusRows: f.paStatusRows.filter((r) => r.id !== id),
      })),
    [],
  );

  /**
   * Deletes a body column and unselects it from every routing rule, so the
   * Routing table can't keep pointing at a block that no longer exists.
   *
   * @param id - Row id of the body column to delete
   */
  const removeBodyColumn = useCallback(
    (id: string) =>
      setForm((f) => {
        const removed = f.content.bodyColumns.find((col) => col.id === id);
        if (!removed) return f;
        const bodyColumns = f.content.bodyColumns.filter(
          (col) => col.id !== id,
        );
        return {
          ...f,
          content: { ...f.content, bodyColumns },
          routingRows: pruneDeletedWhatWeAddLabel(
            f.routingRows,
            removed,
            bodyColumns,
          ),
        };
      }),
    [],
  );

  const addRoutingRow = useCallback(
    () =>
      setForm((f) => ({
        ...f,
        routingRows: [...f.routingRows, createEmptyRoutingRow()],
      })),
    [],
  );

  const updateRoutingRow = useCallback(
    (id: string, changes: Partial<RoutingRuleRow>) =>
      setForm((f) => ({
        ...f,
        routingRows: f.routingRows.map((r) =>
          r.id === id ? { ...r, ...changes } : r,
        ),
      })),
    [],
  );

  const handleDiscard = useCallback(() => {
    setForm(formFromConfig(originalDoc?.text_notes));
  }, [originalDoc]);

  const handleSave = useCallback(async () => {
    if (!orgId.trim()) {
      toast.error("No healthcare facility id; cannot save.");
      return;
    }
    if (!originalDoc) {
      toast.error("No delivery-steps document to update.");
      return;
    }
    setSaving(true);
    try {
      const staleMinutes =
        originalDoc.global?.stale_threshold_minutes ??
        DEFAULT_STALE_AFTER_MINUTES;
      // Round-trip through JSON to drop `undefined` values — Firestore rejects them.
      const textNotes = JSON.parse(
        JSON.stringify({
          ...originalDoc.text_notes,
          enabled: form.enabled,
          action: form.action,
          status_field: form.statusField,
          settings: form.settings,
          document_config: {
            ...originalDoc.text_notes?.document_config,
            title_template: form.documentName,
            status_source_field: form.statusSourceField,
            pa_status_title_options: rowsToPaStatusOptions(form.paStatusRows),
            routing: routingToConfig(form.routingRows),
            content: contentToConfig(
              {
                ...form.content,
                bodyColumns: stampSelectedWhatWeAddLabels(
                  form.content.bodyColumns,
                  form.routingRows,
                ),
              },
              originalDoc.text_notes?.document_config?.content,
            ),
          },
          preconditions: queryValueToRunWhen(form.preconditions, staleMinutes),
          run_when: queryValueToRunWhen(form.conditions, staleMinutes),
        }),
      ) as TextNotesConfig;
      await FirestoreService.updateDocument(
        `api_config/${orgId}/delivery-steps`,
        "v1",
        { text_notes: textNotes },
      );
      setOriginalDoc((prev) =>
        prev ? { ...prev, text_notes: textNotes } : prev,
      );
      setSavedSnapshot(serialize(form));
      toast.success("Text notes configuration saved.");
    } catch {
      toast.error("Failed to save text notes configuration.");
    } finally {
      setSaving(false);
    }
  }, [orgId, originalDoc, form]);

  return {
    form,
    loading,
    saving,
    error,
    isDirty,
    setDocumentName: (value: string) => patch({ documentName: value }),
    setStatusSourceField: (value: string) =>
      patch({ statusSourceField: value }),
    applySettingsForm,
    setContent,
    setPreconditions: (value: QueryGroup) => patch({ preconditions: value }),
    setConditions: (value: QueryGroup) => patch({ conditions: value }),
    updateRow,
    addRow,
    removeRow,
    removeBodyColumn,
    addRoutingRow,
    updateRoutingRow,
    handleDiscard,
    handleSave,
  };
}
